import { Component, computed, inject, input, Signal } from '@angular/core';
import { features } from '@consts/text.const';
import { WorkflowModel } from '@models/workflow.model';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SharedModels } from '@share-models';
import { WorkflowsSelectors } from '@states/workflow/workflow.selector-types';
import { UiDatasetTableComponent, UiNoDataComponent, UiPreloaderComponent } from '@ui';
import { createRunStepsTableConfig, WorkflowStepRow } from './run-steps-table.util';
import { WorkflowActions } from '@states/workflow/workflow.action.types';
import { MatDialog } from '@angular/material/dialog';
import { ViewStructureModalComponent } from 'src/app/shared/modals/view-structure-modal/view-structure-modal.component';
import { filter, Observable, take } from 'rxjs';

@Component({
  selector: 'run-steps-table',
  imports: [
    TranslatePipe,
    UiDatasetTableComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
  ],
  templateUrl: './run-steps-table.component.html',
  styleUrl: './run-steps-table.component.scss'
})
export class RunStepsTableComponent {
  private readonly store$ = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);

  public graphId = input.required<string>();
  public orgId = input.required<string>();
  public runId = input.required<string>();

  protected readonly noData = features.tags.noResults;
  protected readonly viewStates = SharedModels.ViewState;

  public selectIsStepsLoadingSig: Signal<boolean> =
    this.store$.selectSignal(WorkflowsSelectors.selectIsStepsLoading);
  public selectStepsSig: Signal<WorkflowModel.GetRunStepsResponse> =
    this.store$.selectSignal(WorkflowsSelectors.selectSteps);

  public selectStep$: Observable<WorkflowModel.WorkflowRunStepView> =
    this.store$.select(WorkflowsSelectors.selectStep);

  public readonly tableConfig = createRunStepsTableConfig();

  public rowsSig: Signal<WorkflowStepRow[]> = computed(() => {
    const steps = this.selectStepsSig();

    return Object.values(steps?.stepsByNodeId ?? {}).map((step) => {
      const stateLabel = this.translateService.instant(WorkflowModel.StepStatusMap[step.latestState]);
      //temp fix, remove when all nodes will be on BE
      const nodeType = WorkflowModel.NodeTypesMap[step.nodeType] ? this.translateService.instant(WorkflowModel.NodeTypesMap[step.nodeType]) : step.nodeType;

      const stepRow = {
        stepId: step.stepId,
        stateLabel: stateLabel,
        nodeType: nodeType,
      };

      return stepRow;
    })
  });

  public ngOnInit(): void {
    const steps = this.selectStepsSig();

    if (!Object.keys(steps ?? {}).length) {
      this.store$.dispatch(WorkflowActions.getWorkflowRunSteps({
        orgId: this.orgId(),
        graphId: this.graphId(),
        runId: this.runId(),
      }));
    }
  }

  protected readonly viewStateSig = computed(() => {
    if (this.rowsSig()?.length) {
      return SharedModels.ViewState.HAS_CONTENT;
    }

    return SharedModels.ViewState.INITIAL_EMPTY;
  });

  public onRowAction(event: { actionId: string; row: WorkflowStepRow }): void {
    switch (event.actionId) {
      case 'viewFullData':
        this.viewFullData(event.row.stepId);
        break;
    }
  }

  private viewFullData(stepId: string): void {
    this.store$.dispatch(WorkflowActions.getWorkflowRunStepInfo({
      orgId: this.orgId(),
      graphId: this.graphId(),
      runId: this.runId(),
      stepId: stepId,
    }))

    this.selectStep$
      .pipe(
        filter(Boolean),
        take(1),
      )
      .subscribe((step) => {
        if (step) {
          this.dialog
            .open(ViewStructureModalComponent, {
              panelClass: 'thumbnail-dialog-wrapper',
              width: '80vw',
              height: '600px',
              data: step,
            })
            .afterClosed();
        }
      })
  }
}
